import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DataQueryService } from '../data-query.service';
import { Subscription } from 'rxjs';
import { DataQuery } from '../data-query.model';
import { TylerMatTableDataSource } from '../../../shared-components/angular-material/extended.class';
import { DataQueryResultPage } from '../data-query-result-page';
import { LoginService } from '../../../shared-modules/login/login.service';
import { UserService } from '../../../shared-components/user/user.service';
import { User } from '../../../shared-components/user/user.model';
import { DataQueryPermissions } from '../data-query-permissions';
import { detailExpand } from '../../../shared-components/angular-material/mat-animations';
import { MenuItem } from '../../../shared-components/controls/menus/menu-item.model';
import { DeleteQueryDialogComponent } from '../delete-query-dialog/delete-query-dialog.component';
import { take } from 'rxjs/operators';
import { System } from '../../../shared-components/System';

@Component({
    selector: 'view-queries-dialog',
    templateUrl: './view-queries-dialog.component.html',
    styleUrls: ['./view-queries-dialog.component.css'],
    providers: [],
    animations: [
        detailExpand
    ]
})
export class ViewQueriesDialogComponent implements OnInit, AfterViewInit, OnDestroy {

    // Injected Properties
    isStudentSearch: boolean;
    isCourseSearch: boolean;
    isStaffSearch: boolean;

    // Properties
    subscriptions: Subscription[] = [];
    queries: DataQuery[];
    V10: boolean;
    currentUser: User;
    permissions: DataQueryPermissions;
    querySearch = '';
    columns = ['more', 'name', 'scope', 'owner', 'last-updated', 'actions'];

    // Table Properties
    tableData = new TylerMatTableDataSource();
    pageSizeOptions = DataQueryResultPage.pageSizeOptions;
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    expandEmitter = new EventEmitter<boolean>();
    @ViewChild('nameHeader', { read: ElementRef }) nameHeader: ElementRef;

    // Class Functions
    constructor(
        private dialogBox: MatDialogRef<ViewQueriesDialogComponent>,
        private dataQueryService: DataQueryService,
        private loginService: LoginService,
        private userService: UserService,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) private data: any
    ) { }

    get initialPageSize(): number {
        return this.dataQueryService.initialPageSize;
    }

    // Event Functions
    ngOnInit() {
        // PRE: injected data with the injected properties
        // POST: initializes the data

        // Setting the injected and service properties
        if (this.data && this.data.isStudentSearch != undefined) {
            this.isStudentSearch = this.data.isStudentSearch;
        } else {
            this.isStudentSearch = this.dataQueryService.isStudentSearch;
        }

        if (this.data && this.data.isCourseSearch != undefined) {
            this.isCourseSearch = this.data.isCourseSearch;
        } else {
            this.isCourseSearch = this.dataQueryService.isCourseSearch;
        }

        if (this.data && this.data.isStaffSearch != undefined) {
            this.isStaffSearch = this.data.isStaffSearch;
        } else {
            this.isStaffSearch = this.dataQueryService.isStaffSearch;
        }

        // Setting local variables
        this.V10 = this.loginService.LoginSettings.V10;
        this.currentUser = this.userService.currentUser;

        // Obtaining saved queries
        let observer;
        if (this.isStudentSearch) {
            observer = this.dataQueryService.studentFilters;
        } else if (this.isCourseSearch) {
            observer = this.dataQueryService.courseFilters;
        } else if (this.isStaffSearch) {
            observer = this.dataQueryService.staffFilters;
        } else {
            observer = this.dataQueryService.savedQueries;
        }
        observer.pipe(take(1)).subscribe(queries => {
            this.queries = queries;
            this.tableData.data = this.queries;
        });

        // Obtaining permissions
        this.dataQueryService.getPermissions().subscribe(permissions => {
            this.permissions = permissions;
            if (this.permissions.canPublishToIntegration) {
                this.columns.splice(4, 0, 'published');
            }
        });

        // Observing expand and collapse events
        this.expandEmitter.subscribe(expand => {
            this.tableData.expandAll(expand);
        });
    }

    ngAfterViewInit() {
        // POST: sets up the data source and sets the initial sorting
        this.tableData.paginator = this.paginator;
        this.tableData.sort = this.sort;
    }

    ngOnDestroy() {
        // POST: unsubscribes from all subscriptions
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    cancel() {
        // PRE: the cancel button is clicked
        // POST: closes the dialog box
        this.dialogBox.close();
    }

    selectQuery(query: DataQuery) {
        // PRE: a query's name is click and provided to this function
        // POST: closes the dialog box with the query
        this.dialogBox.close(query);
    }

    menuItemClick(item: MenuItem) {
        // PRE: an item on an action menu is clicked
        // POST: performs the proper action
        let query: DataQuery = item.data;
        switch (item.itemName) {

            // Edit Action
            case 'Edit':
                if (query.ID == this.dataQueryService.currentQuery.ID) {
                    query = this.dataQueryService.currentQuery;
                }
                this.dataQueryService.openAddEditQuery(
                    query,
                    true,
                    false,
                    (returnedQuery) => {
                        if (returnedQuery) {
                            for (let i = 0; i < this.queries.length; i++) {
                                if (this.queries[i].ID == returnedQuery.ID) {
                                    Object.assign(this.queries[i], returnedQuery);
                                    this.tableData.data = this.queries;
                                    break;
                                }
                            }
                        }
                    }
                );
                break;

            // Export Action
            case 'Export Query':
                this.subscriptions.push(

                    // Getting the report xml
                    this.dataQueryService.getReportTemplateXML(query.ID).subscribe(reportXML => {

                        // We reload the query XML to capture changes to how we interpret it
                        this.dataQueryService.loadDataFromXML(query);
                        query.QueryXML = this.dataQueryService.getQueryXML(
                            this.dataQueryService.getAllFields(query)
                        );

                        // Setting up the XML document
                        const xml = System.htmlToText(query.QueryXML);
                        const parser = new DOMParser();
                        const xmlDoc = parser.parseFromString(xml, 'text/xml');
                        const exportSettings = xmlDoc.createElement('ExportSettings');
                        const exportType = xmlDoc.createElement('ExportType');
                        exportType.innerHTML = query.ExportType;
                        const delimeter = xmlDoc.createElement('Delimeter');
                        delimeter.innerHTML = query.Delimiter;
                        const otherDelimeter = xmlDoc.createElement('OtherDelimeter');
                        otherDelimeter.innerHTML = query.OtherDelimiter;
                        const includeColumnHeadings = xmlDoc.createElement('IncludeColumnHeadings');
                        includeColumnHeadings.innerHTML = query.IncludeColumnHeadings.toString();
                        xmlDoc.getElementsByTagName('root')[0].appendChild(exportSettings);
                        xmlDoc.getElementsByTagName('ExportSettings')[0].appendChild(exportType);
                        xmlDoc.getElementsByTagName('ExportSettings')[0].appendChild(delimeter);
                        xmlDoc.getElementsByTagName('ExportSettings')[0].appendChild(otherDelimeter);
                        xmlDoc.getElementsByTagName('ExportSettings')[0].appendChild(includeColumnHeadings);

                        // Organizing the query XML
                        let queryXML = new XMLSerializer().serializeToString(xmlDoc);
                        queryXML += reportXML;

                        // Downloading the XML document
                        const a = document.createElement('a');
                        a.setAttribute('href', 'data:text/xml;charset=utf-16,' + encodeURIComponent(queryXML));
                        a.setAttribute('download', query.QueryName + '.xml');
                        a.style.display = 'none';
                        document.body.appendChild(a);
                        if (document.createEvent) {
                            const event = document.createEvent('MouseEvents');
                            event.initEvent('click', true, true);
                            a.dispatchEvent(event);
                        } else {
                            a.click();
                        }
                        document.body.removeChild(a);
                    })
                );
                break;

            // Delete Action
            case 'Delete':
                const dialogBox = this.dialog.open(DeleteQueryDialogComponent, {
                    disableClose: true,
                    data: {
                        query: item.data
                    }
                });
                this.subscriptions.push(
                    dialogBox.afterClosed().subscribe(del => {
                        if (del) {
                            if (this.queries.indexOf(item.data) > -1) {
                                this.queries.splice(this.queries.indexOf(item.data), 1);
                            }
                            this.tableData.data = this.queries;
                            this.applySearch();
                        }
                    })
                );
                break;

            // If we haven't found the proper action, simply do nothing
            default:
                break;
        }
    }

    applySearch() {
        // PRE: the user blurs the search input, presses enter while focused on it, or clicks the magnifying glass
        // POST: applies the search criterion to the filters
        if (this.querySearch) {
            this.tableData.data = this.queries.filter(
                query => query.QueryName.toLowerCase().includes(this.querySearch.toLowerCase())
            );
        } else {
            this.tableData.data = this.queries;
        }
    }

    // Methods
    canEditAndDeleteQuery(query: DataQuery) {
        // PRE: a query to check against
        // POST: returns true if the user can save or delete the query
        return this.permissions && (
            query.OwnerID == this.currentUser.Id
            || (
                query.Scope == 'Selected Sites'
                && this.permissions.canUserModifySchoolQuery
            )
            || (
                query.Scope == 'District'
                && this.permissions.canUserModifyDistrictQuery
            )
        );
    }

    hasDetailRow(query: DataQuery) {
        // POST: returns if a query has a detail row
        return query.Description || (query.SelectedSites && query.Scope == 'Selected Sites');
    }

    getMenuItems(query: DataQuery) {
        // POST: returns the menu items for a query
        const menuItems: string[] = [];
        if (this.canEditAndDeleteQuery(query)) {
            return [
                'Edit',
                'Export Query',
                'Delete'
            ];
        } else {
            return [
                'Export Query'
            ];
        }
    }
}
